import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Check, Minus, ChevronDown, ChevronUp, Wrench, ArrowRight,
  Cloud, Bot, Calculator, Sparkles, Shield, Zap, Users,
  MessageSquare, BarChart3, Globe, Headphones, Clock, Star,
  ShoppingCart, Home, Megaphone, Briefcase,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import CountdownTimer from "../components/CountdownTimer";
import { PricingGuarantee } from "../components/GuaranteeSection";
import UrgencyBar from "../components/UrgencyBar";
import {
  productSchema,
  softwareApplicationSchema,
  pricingFaqPageSchema,
  pricingBreadcrumbSchema,
} from "../data/structuredData";
import { agents } from "../data/agents";

/* ─────────────────────────────────────────────
   AGENT ICON MAP
   ───────────────────────────────────────────── */
const AGENT_ICONS = {
  "customer-support": Headphones,
  "e-commerce": ShoppingCart,
  "real-estate": Home,
  "marketing-agencies": Megaphone,
  "consultancy": Briefcase,
};

/* ─────────────────────────────────────────────
   PLAN DATA — static structure (non-i18n keys)
   ───────────────────────────────────────────── */
const PLAN_STRUCTURES = [
  {
    id: "cloud",
    monthlyPrice: 99,
    annualPrice: 79,
    badge: null,
    featured: false,
    href: "/signup?plan=cloud",
    table: {
      agents: "1",
      channels: "3",
      messages: "10,000",
      dashboard: "Basic",
      api: false,
      sla: "\u2014",
      support: "Community",
      onboarding: false,
      trial: true,
    },
  },
  {
    id: "multi-agent",
    monthlyPrice: 249,
    annualPrice: 199,
    badge: "RECOMMENDED",
    featured: true,
    href: "/signup?plan=multi-agent",
    table: {
      agents: "Up to 5",
      channels: "Unlimited",
      messages: "50,000",
      dashboard: "Full + analytics",
      api: true,
      sla: "48h",
      support: "Priority email",
      onboarding: false,
      trial: true,
    },
  },
  {
    id: "enterprise",
    monthlyPrice: 0,
    annualPrice: 0,
    badge: null,
    featured: false,
    href: "mailto:sales@maxemployee.com",
    table: {
      agents: "Unlimited",
      channels: "Unlimited",
      messages: "Unlimited",
      dashboard: "Enterprise + API",
      api: true,
      sla: "4h",
      support: "Dedicated manager",
      onboarding: true,
      trial: true,
    },
  },
];

const TABLE_ROWS_KEYS = [
  { key: "agents", tableKey: "agents", icon: <Users className="w-3.5 h-3.5" /> },
  { key: "channels", tableKey: "channels", icon: <MessageSquare className="w-3.5 h-3.5" /> },
  { key: "messages", tableKey: "messages", icon: <BarChart3 className="w-3.5 h-3.5" /> },
  { key: "skills", tableKey: "skills", icon: <Sparkles className="w-3.5 h-3.5" /> },
  { key: "dashboard", tableKey: "dashboard", icon: <Globe className="w-3.5 h-3.5" /> },
  { key: "api", tableKey: "api", icon: <Zap className="w-3.5 h-3.5" /> },
  { key: "sla", tableKey: "sla", icon: <Clock className="w-3.5 h-3.5" /> },
  { key: "support", tableKey: "support", icon: <Headphones className="w-3.5 h-3.5" /> },
  { key: "onboarding", tableKey: "onboarding", icon: <Star className="w-3.5 h-3.5" /> },
  { key: "trial", tableKey: "trial", icon: <Shield className="w-3.5 h-3.5" /> },
];

/* ─────────────────────────────────────────────
   Sub-components
   ───────────────────────────────────────────── */

function FeatureCell({ value }) {
  const { t } = useTranslation();
  if (typeof value === "boolean") {
    return value ? (
      <span className="flex items-center justify-center" aria-label={t('pricing.included')}>
        <Check className="w-4 h-4 text-amber-500" />
      </span>
    ) : (
      <span className="flex items-center justify-center" aria-label={t('pricing.not_included')}>
        <Minus className="w-4 h-4 text-zinc-600" />
      </span>
    );
  }
  return <span className="text-zinc-300 text-sm">{value}</span>;
}

function FAQItem({ item, isOpen, onToggle }) {
  const slug = item.q.replace(/\s+/g, "-").toLowerCase().replace(/[^a-z0-9-]/g, "");
  const btnId = `faq-btn-${slug}`;
  const panelId = `faq-panel-${slug}`;
  return (
    <div className="border-b border-zinc-800">
      <button
        id={btnId}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className="w-full flex items-center justify-between py-5 text-left group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090B] rounded-sm"
      >
        <span className="text-white text-sm font-medium pr-4">{item.q}</span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-amber-500 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" aria-hidden="true" />
        )}
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
      >
        <div id={panelId} role="region" aria-labelledby={btnId}>
          <p className="text-zinc-400 text-sm leading-relaxed pb-5 pr-8">{item.a}</p>
        </div>
      </div>
    </div>
  );
}

function ROICalculator({ t }) {
  const [hours, setHours] = useState(10);
  const [costPerHour, setCostPerHour] = useState(25);

  const savings = useMemo(() => {
    const monthly = hours * 4 * costPerHour * 0.7;
    const annual = monthly * 12;
    return { monthly: Math.round(monthly), annual: Math.round(annual) };
  }, [hours, costPerHour]);

  return (
    <div className="border border-zinc-800 bg-zinc-950/80 p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
          <Calculator className="w-5 h-5 text-amber-500" />
        </div>
        <div>
          <h3 className="font-display text-xl text-white uppercase">{t('pricing_page.roi_calculator_title')}</h3>
          <p className="text-zinc-500 text-xs font-mono uppercase tracking-wider">
            {t('pricing_page.roi_subtitle')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2">
            {t('pricing_page.roi_hours_label')}
          </label>
          <input
            type="range"
            min="1"
            max="40"
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="w-full accent-amber-500"
            aria-label={t('pricing_page.roi_hours_label')}
          />
          <div className="flex justify-between text-xs text-zinc-500 mt-1">
            <span>1h</span>
            <span className="text-amber-500 font-display text-lg">{hours}h</span>
            <span>40h</span>
          </div>
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2">
            {t('pricing_page.roi_cost_label')}
          </label>
          <input
            type="range"
            min="10"
            max="100"
            value={costPerHour}
            onChange={(e) => setCostPerHour(Number(e.target.value))}
            className="w-full accent-amber-500"
            aria-label={t('pricing_page.roi_cost_label')}
          />
          <div className="flex justify-between text-xs text-zinc-500 mt-1">
            <span>$10</span>
            <span className="text-amber-500 font-display text-lg">${costPerHour}</span>
            <span>$100</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-zinc-900/60 border border-zinc-800 p-4 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">
            {t('pricing_page.roi_monthly_savings')}
          </p>
          <p className="font-display text-2xl sm:text-3xl text-emerald-400">
            ${savings.monthly.toLocaleString()}
          </p>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 p-4 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">
            {t('pricing_page.roi_annual_savings')}
          </p>
          <p className="font-display text-2xl sm:text-3xl text-emerald-400">
            ${savings.annual.toLocaleString()}
          </p>
        </div>
      </div>

      <p className="mt-4 text-zinc-500 text-xs text-center">
        {t('pricing_page.roi_disclaimer')}
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main Pricing Page
   ───────────────────────────────────────────── */
export default function Pricing() {
  const { t } = useTranslation();
  const [annual, setAnnual] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);
  const [compareMode, setCompareMode] = useState(false);

  const TABLE_ROWS = useMemo(() => TABLE_ROWS_KEYS.map((r) => ({
    ...r,
    label: t(`pricing_page.table_${r.tableKey}`),
  })), [t]);

  /* Plan data with i18n */
  const plansData = useMemo(() => [
    {
      ...PLAN_STRUCTURES[0],
      name: t('pricing_page.cloud_name'),
      subtitle: t('pricing_page.cloud_subtitle'),
      description: t('pricing_page.cloud_description'),
      features: [
        t('pricing_page.cloud_f1'),
        t('pricing_page.cloud_f2'),
        t('pricing_page.cloud_f3'),
        t('pricing_page.cloud_f4'),
        t('pricing_page.cloud_f5'),
        t('pricing_page.cloud_f6'),
        t('pricing_page.cloud_f7'),
      ],
      cta: t('pricing_page.cloud_cta'),
    },
    {
      ...PLAN_STRUCTURES[1],
      name: t('pricing_page.multi_name'),
      subtitle: t('pricing_page.multi_subtitle'),
      description: t('pricing_page.multi_description'),
      features: [
        t('pricing_page.multi_f1'),
        t('pricing_page.multi_f2'),
        t('pricing_page.multi_f3'),
        t('pricing_page.multi_f4'),
        t('pricing_page.multi_f5'),
        t('pricing_page.multi_f6'),
        t('pricing_page.multi_f7'),
        t('pricing_page.multi_f8'),
      ],
      cta: t('pricing_page.multi_cta'),
    },
    {
      ...PLAN_STRUCTURES[2],
      name: t('pricing_page.enterprise_name'),
      subtitle: t('pricing_page.enterprise_subtitle'),
      description: t('pricing_page.enterprise_description'),
      features: [
        t('pricing_page.enterprise_f1'),
        t('pricing_page.enterprise_f2'),
        t('pricing_page.enterprise_f3'),
        t('pricing_page.enterprise_f4'),
        t('pricing_page.enterprise_f5'),
        t('pricing_page.enterprise_f6'),
        t('pricing_page.enterprise_f7'),
        t('pricing_page.enterprise_f8'),
      ],
      cta: t('pricing_page.contact_sales'),
    },
  ], [t]);

  const faqItems = useMemo(() => [
    { q: t('pricing_page.faq_q1'), a: t('pricing_page.faq_a1') },
    { q: t('pricing_page.faq_q2'), a: t('pricing_page.faq_a2') },
    { q: t('pricing_page.faq_q3'), a: t('pricing_page.faq_a3') },
    { q: t('pricing_page.faq_q4'), a: t('pricing_page.faq_a4') },
    { q: t('pricing_page.faq_q5'), a: t('pricing_page.faq_a5') },
    { q: t('pricing_page.faq_q6'), a: t('pricing_page.faq_a6') },
    { q: t('pricing_page.faq_q7'), a: t('pricing_page.faq_a7') },
    { q: t('pricing_page.faq_q8'), a: t('pricing_page.faq_a8') },
  ], [t]);

  const toggleFaq = (idx) => {
    setOpenFaq((prev) => (prev === idx ? null : idx));
  };

  // Comparison table data with i18n
  const comparisonData = useMemo(() => [
    { feature: t('pricing_page.compare_setup'), cloud: t('pricing_page.compare_setup_cloud'), mp: t('pricing_page.compare_setup_mp') },
    { feature: t('pricing_page.compare_configuration'), cloud: t('pricing_page.compare_config_cloud'), mp: t('pricing_page.compare_config_mp') },
    { feature: t('pricing_page.compare_industries'), cloud: t('pricing_page.compare_industries_cloud'), mp: t('pricing_page.compare_industries_mp') },
    { feature: t('pricing_page.compare_channels'), cloud: t('pricing_page.compare_channels_cloud'), mp: t('pricing_page.compare_channels_mp') },
    { feature: t('pricing_page.compare_custom_skills'), cloud: t('pricing_page.compare_skills_cloud'), mp: t('pricing_page.compare_skills_mp') },
    { feature: t('pricing_page.compare_price'), cloud: t('pricing_page.from_99'), mp: t('pricing_page.from_99') },
    { feature: t('pricing_page.compare_best_for'), cloud: t('pricing_page.compare_best_cloud'), mp: t('pricing_page.compare_best_mp') },
  ], [t]);

  // Enterprise features with i18n
  const enterpriseFeatures = useMemo(() => [
    t('pricing_page.enterprise_f1'),
    t('pricing_page.enterprise_f2'),
    t('pricing_page.enterprise_f3'),
    t('pricing_page.enterprise_f4'),
    t('pricing_page.enterprise_f5'),
    t('pricing_page.enterprise_f6'),
    t('pricing_page.enterprise_f7'),
    t('pricing_page.enterprise_f8'),
  ], [t]);

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title="Pricing | Max OS Cloud Plans — Starter to Enterprise"
        description="Compare Max OS Cloud plans: Single Agent from $99/mo, Multi-Agent from $249/mo, Enterprise custom. 7-day free trial. Cancel anytime."
        url="/pricing"
        structuredData={[productSchema, softwareApplicationSchema, pricingFaqPageSchema, pricingBreadcrumbSchema]}
      />
      <Header />
      <UrgencyBar />

      <main id="main-content">
        {/* === SECTION 1: HERO === */}
        <section className="relative overflow-hidden border-b border-zinc-900" aria-labelledby="pricing-hero-heading">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" aria-hidden="true" />
          <div className="absolute inset-0 grid-bg opacity-40" aria-hidden="true" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-16 text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-6" aria-hidden="true">
              {t('pricing_page.section_label')}
            </div>
            <h1 id="pricing-hero-heading" className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl uppercase leading-[0.95] max-w-4xl mx-auto">
              {t('pricing_page.hero_title_1')}
              <br />
              <span className="text-amber-500">{t('pricing_page.hero_title_2')}</span>
            </h1>
            <p className="mt-6 max-w-xl mx-auto text-lg text-zinc-400 leading-relaxed">
              {t('pricing_page.hero_subtitle')}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 px-4 py-2 rounded-full">
              <span className="text-sm text-amber-400">{t('pricing_page.hero_trial_note')}</span>
            </div>
            <CountdownTimer />
          </div>
        </section>

        {/* === SECTION 2: BILLING TOGGLE === */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-12" aria-label={t('pricing.billing_period_toggle')}>
          <div className="flex items-center justify-center gap-4">
            <span className={`font-mono text-xs uppercase tracking-[0.2em] ${!annual ? "text-white" : "text-zinc-400"}`}>
              {t('pricing_page.monthly')}
            </span>
            <button
              onClick={() => setAnnual(!annual)}
              role="switch"
              aria-checked={annual}
              aria-label={annual ? t('pricing_page.switch_to_monthly') : t('pricing_page.switch_to_annual')}
              className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${annual ? "bg-amber-500" : "bg-zinc-700"}`}
            >
              <div
                className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-transform duration-300 ${annual ? "translate-x-7" : "translate-x-0.5"}`}
                aria-hidden="true"
              />
            </button>
            <span className={`font-mono text-xs uppercase tracking-[0.2em] ${annual ? "text-amber-500" : "text-zinc-400"}`}>
              {t('pricing_page.annual')} <span className="text-emerald-400">({t('pricing_page.save_20')})</span>
            </span>
          </div>
        </section>

        {/* === SECTION 3: PRICING CARDS === */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-10 pb-8" aria-labelledby="plans-heading">
          <h2 id="plans-heading" className="sr-only">{t('pricing.available_plans')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {plansData.map((plan) => {
              const price = plan.monthlyPrice > 0
                ? (annual ? plan.annualPrice : plan.monthlyPrice)
                : null;
              return (
                <div
                  key={plan.id}
                  className={`relative p-8 flex flex-col transition-all duration-300 ${
                    plan.featured
                      ? "bg-zinc-950 border-2 border-amber-500 shadow-[0_0_40px_rgba(245,158,11,0.1)] scale-[1.02] md:scale-105"
                      : "bg-zinc-950/80 border border-zinc-800 hover:border-zinc-700 hover:shadow-[0_0_20px_rgba(245,158,11,0.05)]"
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-px left-1/2 -translate-x-1/2 bg-amber-500 text-black px-4 py-1 font-mono text-[10px] uppercase tracking-[0.2em]">
                      {t('pricing_page.recommended')}
                    </div>
                  )}

                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-3" aria-hidden="true">
                    // {plan.subtitle.toUpperCase()}
                  </div>

                  <h3 className="font-display text-2xl text-white uppercase leading-tight">
                    {plan.name}
                  </h3>
                  <p className="text-amber-500 font-mono text-xs uppercase tracking-[0.15em] mt-1">
                    {plan.subtitle}
                  </p>

                  <p className="text-zinc-400 text-sm mt-3 leading-relaxed">
                    {plan.description}
                  </p>

                  <div className="mt-6 mb-8">
                    {price !== null ? (
                      <>
                        <span className="font-display text-5xl text-white">${price}</span>
                        <span className="text-zinc-400 text-sm ml-1">{t('pricing_page.per_month')}</span>
                        {annual && (
                          <div className="font-mono text-[10px] text-emerald-400 mt-1 uppercase tracking-wider">
                            {t('pricing_page.billed_annually')}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="font-display text-3xl text-white">{t('pricing_page.custom')}</span>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8 flex-1" role="list">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" aria-hidden="true" />
                        <span className="text-zinc-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={plan.href}
                    className={`block text-center py-3.5 px-6 font-mono text-xs uppercase tracking-[0.15em] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 ${
                      plan.featured
                        ? "bg-amber-500 hover:bg-amber-400 text-black font-bold hover:scale-105 hover:shadow-[0_0_20px_rgba(245,158,11,0.3)]"
                        : "border border-zinc-700 hover:border-amber-500 text-zinc-300 hover:text-amber-500 hover:scale-105 hover:shadow-[0_0_20px_rgba(245,158,11,0.15)]"
                    }`}
                  >
                    {plan.cta}
                  </Link>
                  <p className="mt-3 font-mono text-[9px] uppercase tracking-wider text-zinc-500">
                    {plan.id === "enterprise"
                      ? t('pricing_page.enterprise_note')
                      : t('pricing_page.trial_note')}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* === SECTION 4: INTERACTIVE COMPARISON TOGGLE === */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8" aria-labelledby="compare-heading">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
            <h2 id="compare-heading" className="font-display text-2xl sm:text-3xl text-white uppercase">
              {t('pricing_page.compare_features')}
            </h2>
            <button
              onClick={() => setCompareMode(!compareMode)}
              className={`font-mono text-xs uppercase tracking-[0.15em] px-4 py-2 border transition-all ${
                compareMode
                  ? "border-amber-500 text-amber-500 bg-amber-500/10"
                  : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
              }`}
            >
              {compareMode ? `✓ ${t('pricing_page.comparison_on')}` : t('pricing_page.show_comparison')}
            </button>
          </div>

          {/* Desktop: full table */}
          <div className={`overflow-x-auto transition-all duration-500 ${compareMode ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"}`}>
            <table className="w-full border border-zinc-800 min-w-[600px]" aria-label={t('pricing.plan_feature_comparison')}>
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 px-4 sm:px-6 py-4">
                    {t('pricing_page.table_feature')}
                  </th>
                  {PLAN_STRUCTURES.map((plan) => (
                    <th
                      key={plan.id}
                      className={`text-center font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-4 ${
                        plan.featured ? "text-amber-500" : "text-zinc-400"
                      }`}
                    >
                      {plan.featured ? t('pricing_page.multi_subtitle') : plan.id === "cloud" ? t('pricing_page.cloud_subtitle') : t('pricing_page.enterprise_subtitle')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TABLE_ROWS.map((row, rowIdx) => (
                  <tr
                    key={row.key}
                    className={`border-b border-zinc-800/50 transition-colors hover:bg-zinc-900/50 ${
                      rowIdx % 2 === 0 ? "bg-zinc-950/30" : ""
                    }`}
                  >
                    <td className="px-4 sm:px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <span className="text-amber-500/60" aria-hidden="true">{row.icon}</span>
                        {row.label}
                      </div>
                    </td>
                    {PLAN_STRUCTURES.map((plan) => (
                      <td
                        key={plan.id}
                        className={`text-center px-4 py-4 ${plan.featured ? "bg-amber-500/[0.03]" : ""}`}
                      >
                        <FeatureCell value={plan.table[row.key]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: stacked cards (always visible) */}
          <div className="md:hidden mt-6 space-y-4">
            {PLAN_STRUCTURES.map((plan) => (
              <div
                key={plan.id}
                className={`border p-5 ${plan.featured ? "border-amber-500 bg-amber-500/[0.03]" : "border-zinc-800 bg-zinc-950/60"}`}
              >
                <h3 className="font-display text-lg text-white uppercase mb-1">
                  {plan.featured ? t('pricing_page.multi_subtitle') : plan.id === "cloud" ? t('pricing_page.cloud_subtitle') : t('pricing_page.enterprise_subtitle')}
                </h3>
                <div className="space-y-2 mt-3">
                  {TABLE_ROWS.map((row) => (
                    <div key={row.key} className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400">{row.label}</span>
                      <FeatureCell value={plan.table[row.key]} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* === SECTION 5: ROI CALCULATOR === */}
        <section className="max-w-3xl mx-auto px-6 lg:px-12 py-8" aria-labelledby="roi-heading">
          <div className="text-center mb-8">
            <h2 id="roi-heading" className="font-display text-2xl sm:text-3xl text-white uppercase">
              {t('pricing_page.roi_title_1')} <span className="text-amber-500">{t('pricing_page.roi_title_2')}</span>
            </h2>
            <p className="mt-2 text-zinc-400 text-sm">
              {t('pricing_page.roi_subtitle')}
            </p>
          </div>
          <ROICalculator t={t} />
        </section>

        {/* === SECTION 6: CLOUD VS MARKETPLACE === */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 border-t border-zinc-900" aria-labelledby="cloud-vs-mp-heading">
          <div className="text-center mb-10">
            <h2 id="cloud-vs-mp-heading" className="font-display text-3xl md:text-4xl uppercase leading-tight">
              {t('pricing_page.path_title')}
            </h2>
            <p className="mt-3 text-zinc-400 text-lg">
              {t('pricing_page.path_subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <div className="border border-zinc-800 rounded-xl p-8 flex flex-col hover:border-zinc-700 transition-colors">
              <Cloud className="w-8 h-8 text-amber-500 mb-4" aria-hidden="true" />
              <h3 className="font-display text-2xl text-white uppercase mb-1">{t('pricing_page.cloud_path_title')}</h3>
              <p className="text-amber-500 font-mono text-xs uppercase tracking-[0.2em] mb-4">{t('pricing_page.cloud_path_subtitle')}</p>
              <p className="text-zinc-400 leading-relaxed mb-4">
                {t('pricing_page.cloud_path_desc')}
              </p>
              <p className="text-zinc-500 text-sm mb-6">
                <span className="text-zinc-300">{t('pricing_page.ideal_for')}</span> {t('pricing_page.cloud_ideal')}
              </p>
              <div className="mt-auto">
                <span className="font-display text-2xl text-white">{t('pricing_page.from_99')}</span>
              </div>
              <button
                onClick={() => {
                  document.getElementById("plans-heading")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="mt-4 inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 font-mono text-xs uppercase tracking-[0.15em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
              >
                {t('pricing_page.view_plans')} <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>

            <div className="border border-amber-500/30 rounded-xl p-8 flex flex-col hover:border-amber-500/50 transition-colors">
              <Bot className="w-8 h-8 text-amber-500 mb-4" aria-hidden="true" />
              <h3 className="font-display text-2xl text-white uppercase mb-1">{t('pricing_page.mp_path_title')}</h3>
              <p className="text-amber-500 font-mono text-xs uppercase tracking-[0.2em] mb-4">{t('pricing_page.mp_path_subtitle')}</p>
              <p className="text-zinc-400 leading-relaxed mb-4">
                {t('pricing_page.mp_path_desc')}
              </p>
              <p className="text-zinc-500 text-sm mb-6">
                <span className="text-zinc-300">{t('pricing_page.ideal_for')}</span> {t('pricing_page.mp_ideal')}
              </p>
              <div className="mt-auto">
                <span className="font-display text-2xl text-white">{t('pricing_page.from_99')}</span>
              </div>
              <Link
                to="/marketplace"
                className="mt-4 inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 font-mono text-xs uppercase tracking-[0.15em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
              >
                {t('pricing_page.browse_agents')} <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full border border-zinc-800 min-w-[400px]" aria-label={t('pricing.cloud_vs_marketplace_comparison')}>
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 px-4 sm:px-6 py-4">{t('pricing_page.table_feature')}</th>
                  <th className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 px-4 py-4">{t('pricing_page.cloud_path_title')}</th>
                  <th className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 px-4 py-4">{t('pricing_page.mp_path_title')}</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => (
                  <tr key={row.feature} className={`border-b border-zinc-800/50 ${i % 2 === 0 ? "bg-zinc-950/30" : ""}`}>
                    <td className="px-4 sm:px-6 py-3 text-sm text-zinc-400 font-mono text-[11px] uppercase tracking-wider">{row.feature}</td>
                    <td className="text-center px-4 py-3 text-sm text-zinc-300">{row.cloud}</td>
                    <td className="text-center px-4 py-3 text-sm text-zinc-300">{row.mp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-6 text-center text-zinc-500 text-sm">
            {t('pricing_page.not_sure')}
          </p>
        </section>

        {/* === SECTION 6.5: AGENT MARKETPLACE PRODUCTS === */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 border-t border-zinc-900" aria-labelledby="agent-products-heading">
          <div className="text-center mb-12">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4" aria-hidden="true">
              // AGENT MARKETPLACE
            </div>
            <h2 id="agent-products-heading" className="font-display text-3xl md:text-4xl uppercase leading-tight">
              {t('pricing_page.agent_products_title_1')}<br /><span className="text-amber-500">{t('pricing_page.agent_products_title_2')}</span>
            </h2>
            <p className="mt-3 text-zinc-400 text-lg max-w-2xl mx-auto">
              {t('pricing_page.agent_products_subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {AGENTS.map((agent) => {
              const Icon = AGENT_ICONS[agent.id];
              return (
                <div
                  key={agent.id}
                  className="border border-zinc-800 bg-zinc-950/80 p-8 flex flex-col hover:border-amber-500/30 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-amber-500 mb-4" aria-hidden="true" />
                  <h3 className="font-display text-xl text-white uppercase mb-1">{agent.name}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed mb-4">{agent.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {agent.features.map((f) => (
                      <span key={f} className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-500 border border-zinc-800 px-2 py-1">
                        {f}
                      </span>
                    ))}
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-4">
                    {agent.channels.join(' · ')}
                  </p>
                  <div className="mt-auto">
                    <span className="font-display text-3xl text-amber-500">${agent.price}</span>
                    <span className="text-zinc-400 text-sm ml-1">/mo</span>
                  </div>
                  <Link
                    to={`/marketplace/${agent.slug}`}
                    className="mt-4 block text-center py-3 px-6 font-mono text-xs uppercase tracking-[0.15em] border border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                  >
                    {t('pricing_page.get_agent')} <ArrowRight className="w-3 h-3 inline ml-1" aria-hidden="true" />
                  </Link>
                </div>
              );
            })}
          </div>
        </section>

        {/* === SECTION 7: TESTIMONIALS === */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 border-t border-zinc-900" aria-labelledby="testimonials-heading">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 px-4 py-2 rounded-full mb-6">
              <span className="text-sm text-amber-400" aria-hidden="true">🚀</span>
              <span className="text-sm text-amber-400">{t('pricing_page.early_access')}</span>
            </div>
            <h2 id="testimonials-heading" className="font-display text-3xl md:text-4xl uppercase leading-tight">
              {t('pricing_page.testimonials_title_1')}<br /><span className="text-amber-500">{t('pricing_page.testimonials_title_2')}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: t('pricing_page.testimonial_1_quote'),
                name: 'James Mitchell',
                role: t('pricing_page.testimonial_1_role'),
              },
              {
                quote: t('pricing_page.testimonial_2_quote'),
                name: 'Rebecca Santos',
                role: t('pricing_page.testimonial_2_role'),
              },
              {
                quote: t('pricing_page.testimonial_3_quote'),
                name: 'David Park',
                role: t('pricing_page.testimonial_3_role'),
              },
            ].map((testimonial) => (
              <div key={testimonial.name} className="bg-zinc-950/80 border border-zinc-800 p-8 flex flex-col hover:border-zinc-700 transition-colors">
                <p className="text-zinc-200 leading-relaxed text-lg flex-1">&ldquo;{testimonial.quote}&rdquo;</p>
                <div className="mt-8 flex items-center gap-4 pt-6 border-t border-zinc-800">
                  <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0" aria-hidden="true">
                    <span className="font-display text-sm text-amber-500 uppercase">{testimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="font-display text-sm uppercase text-white">{testimonial.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-1">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* === SECTION 8: ENTERPRISE CARD === */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-16" aria-labelledby="enterprise-heading">
          <div className="relative overflow-hidden border border-zinc-800 bg-gradient-to-br from-zinc-950 to-zinc-900 p-8 lg:p-12">
            <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" aria-hidden="true" />
            <div className="relative">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-3" aria-hidden="true">
                {t('pricing_page.enterprise_section_label')}
              </div>
              <h2 id="enterprise-heading" className="font-display text-3xl lg:text-4xl text-white uppercase leading-tight max-w-2xl">
                {t('pricing_page.enterprise_title_1')}
                <br />
                {t('pricing_page.enterprise_title_2')}
              </h2>
              <p className="mt-4 text-zinc-400 max-w-xl leading-relaxed">
                {t('pricing_page.enterprise_desc')}
              </p>
              <div className="mt-6">
                <span className="font-display text-3xl text-white">{t('pricing_page.custom_pricing')}</span>
                <span className="text-zinc-400 text-sm ml-2">{t('pricing_page.tailored_needs')}</span>
              </div>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                {enterpriseFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-amber-500 shrink-0" aria-hidden="true" />
                    <span className="text-zinc-300 text-sm">{f}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <a
                  href="mailto:sales@maxemployee.com"
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-8 py-4 font-bold uppercase tracking-wider text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                >
                  {t('pricing_page.contact_sales')}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* === SECTION 9: SETUP ASSIST === */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-16" aria-labelledby="setup-assist-heading">
          <div className="flex flex-col lg:flex-row items-start gap-6 border border-zinc-800 bg-zinc-950/60 p-8">
            <div className="shrink-0" aria-hidden="true">
              <div className="w-12 h-12 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-amber-500" />
              </div>
            </div>
            <div>
              <h3 id="setup-assist-heading" className="font-display text-2xl text-white uppercase">
                {t('pricing_page.setup_assist_title')}{" "}
                <span className="text-zinc-400 font-mono text-sm normal-case lowercase">
                  {t('pricing_page.setup_assist_optional')}
                </span>
              </h3>
              <p className="mt-3 text-zinc-400 leading-relaxed max-w-2xl">
                {t('pricing_page.setup_assist_desc')}
              </p>
              <div className="mt-4 flex flex-wrap gap-6">
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{t('pricing_page.single_agent')}</span>
                  <div className="font-display text-2xl text-white mt-1">
                    $49<span className="text-sm text-zinc-400 font-sans"> {t('pricing_page.one_time')}</span>
                  </div>
                </div>
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{t('pricing_page.multi_agent')}</span>
                  <div className="font-display text-2xl text-white mt-1">
                    $149<span className="text-sm text-zinc-400 font-sans"> {t('pricing_page.one_time')}</span>
                  </div>
                </div>
              </div>
              <p className="mt-4 text-zinc-400 text-sm italic">
                {t('pricing_page.skip_setup')}
              </p>
            </div>
          </div>
        </section>

        {/* GUARANTEE */}
        <PricingGuarantee />

        {/* === SECTION 10: FAQ === */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-16" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="font-display text-3xl text-white uppercase text-center mb-10">
            {t('pricing_page.faq_title')}
          </h2>
          <div className="border-t border-zinc-800">
            {faqItems.map((item, idx) => (
              <FAQItem
                key={idx}
                item={item}
                isOpen={openFaq === idx}
                onToggle={() => toggleFaq(idx)}
              />
            ))}
          </div>
        </section>

        {/* === SECTION 11: FINAL CTA === */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-20" aria-labelledby="final-cta-heading">
          <div className="text-center">
            <h2 id="final-cta-heading" className="font-display text-3xl lg:text-5xl text-white uppercase leading-tight">
              {t('pricing_page.final_title_1')}
              <br />
              <span className="text-amber-500">{t('pricing_page.final_title_2')}</span>
            </h2>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/register"
                className="bg-amber-500 hover:bg-amber-400 text-black px-8 py-4 font-bold uppercase tracking-wider text-sm transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(245,158,11,0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090B]"
              >
                {t('pricing_page.start_free_trial')}
              </Link>
              <a
                href="mailto:sales@maxemployee.com"
                className="border border-zinc-700 hover:border-amber-500 text-zinc-300 hover:text-amber-500 px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] transition-all hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090B]"
              >
                {t('pricing_page.talk_to_sales')}
              </a>
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              {t('pricing_page.final_note')}
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
